import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { combineClassnames } from 'app/common/presentation'
import { useUser } from 'app/user/application'
import { User, Workspace } from 'app/user/types'

import { ActivityIndicator, MenuItem } from 'components'

import styles from './Profile.module.scss'

interface WorkspaceMenuProps {
  workspace: Workspace
  user: User
}

type ChangeWorkspaceState = 'changing' | 'error'

const workspaceAvatarClasses = ['purple', 'teal', 'orange']

const WorkspaceMenu = ({ workspace: selectedWorkspace, user }: WorkspaceMenuProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('sidebar')

  const { changeWorkspace } = useUser()

  const [changeWorkspaceState, setChangeWorkspaceState] = useState<
    ChangeWorkspaceState | undefined
  >()
  const [changingWorkspaceId, setChangingWorkspaceId] = useState<string>()

  const activeWorkspaces = user.workspaces.filter((workspace) => workspace.status === 'active')
  const workspaces = activeWorkspaces.length > 0 ? activeWorkspaces : [selectedWorkspace]
  const handleChangeWorkspace = (id: string) => {
    setChangingWorkspaceId(id)
    setChangeWorkspaceState('changing')
    void changeWorkspace(id, async (success) => {
      setChangeWorkspaceState(success ? undefined : 'error')
      if (success) {
        await navigate('/')
      }
    })
  }

  const renderChangeWorkspaceIndicator = () => {
    switch (changeWorkspaceState) {
      case 'changing':
        return (
          <span className={styles.workspaceIndicator}>
            <ActivityIndicator size="xxs" />
          </span>
        )
      case 'error':
        return <span className={styles.error}>{t('profile.menu.changeError')}</span>
      default:
        return undefined
    }
  }

  return (
    <div className={styles.workspaceMenu}>
      <div className={styles.workspaceList}>
        {workspaces.map((workspace, index) => (
          <MenuItem
            className={styles.workspaceItem}
            key={workspace.id}
            label={
              <span className={styles.workspaceItemLabel}>
                <span className={styles.workspaceItemName}>{workspace.name}</span>
                {workspace.id === changingWorkspaceId && changeWorkspaceState === 'changing'
                  ? renderChangeWorkspaceIndicator()
                  : null}
              </span>
            }
            leading={
              <span
                className={combineClassnames(
                  styles.workspaceMenuIcon,
                  styles[workspaceAvatarClasses[index % 3]],
                )}
              >
                {workspace.name.slice(0, 1).toUpperCase()}
              </span>
            }
            onClick={
              workspace.id !== selectedWorkspace.id
                ? () => handleChangeWorkspace(workspace.id)
                : undefined
            }
          />
        ))}
        {changeWorkspaceState === 'error' && renderChangeWorkspaceIndicator()}
      </div>
    </div>
  )
}

export default WorkspaceMenu
